import React, { useState, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { searchUsers, exportUsersCSV } from '../utils/api';
import { formatDistanceToNow } from '../utils/helpers';
import { Search, Users, Crown, Building2, Download } from 'lucide-react';

export default function UserSearchPage() {
  const navigate    = useNavigate();
  const [query,     setQuery]     = useState('');
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [searched,  setSearched]  = useState(false);
  const debounceRef = useRef(null);

  const doSearch = useCallback(async (q) => {
    if (q.trim().length < 2) { setUsers([]); setSearched(false); return; }
    setLoading(true);
    setSearched(true);
    try {
      const data = await searchUsers(q.trim());
      setUsers(data.users || []);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { clearTimeout(debounceRef.current); doSearch(query); }
  };

  return (
    <Layout title="User Search" crumb={searched ? `${users.length} result${users.length !== 1 ? 's' : ''}` : undefined}>

      {/* Search bar */}
      <div style={{ maxWidth: 560, margin: '0 auto 28px' }}>
        <div className="search-wrap" style={{ position: 'relative' }}>
          <Search size={15} />
          <input
            className="search-input"
            style={{ fontSize: '0.95rem', padding: '12px 14px 12px 38px' }}
            placeholder="Search by name or email address…"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        </div>
        <p style={{ marginTop: 8, fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', textAlign: 'center', letterSpacing: '0.04em' }}>
          Searches across all organizations · min 2 characters
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={async () => { try { await exportUsersCSV(); toast.success('Users exported'); } catch { toast.error('Export failed'); } }}
            title="Export all users to CSV"
          >
            <Download size={13} /> Export All Users
          </button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="loading-center"><div className="spinner" /> Searching…</div>
      ) : searched && users.length === 0 ? (
        <div className="empty">
          <Users size={28} />
          <h3>No users found</h3>
          <p>Try a different name or email address.</p>
        </div>
      ) : users.length > 0 ? (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Organization</th>
                  <th>Access</th>
                  <th>Status</th>
                  <th className="col-hide-mobile">Last Login</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr
                    key={user.id}
                    style={{ cursor: 'pointer', opacity: user.is_active ? 1 : 0.5 }}
                    onClick={() => navigate(`/orgs/${user.organization_id}`)}
                    title={`Go to ${user.org_name}`}
                  >
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        {user.access_level === 'super_admin' && <Crown size={12} style={{ color: 'var(--gold)', flexShrink: 0 }} />}
                        <span style={{ fontWeight: 600 }}>{user.first_name} {user.last_name}</span>
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: '0.75rem' }}>{user.email}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Building2 size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.82rem' }}>{user.org_name}</span>
                      </div>
                    </td>
                    <td>
                      {user.access_level === 'super_admin' && <span className="badge badge-gold">Super Admin</span>}
                      {user.access_level === 'admin'       && <span className="badge badge-blue">Admin</span>}
                      {(!user.access_level || user.access_level === 'standard') && <span className="badge badge-dim">Standard</span>}
                    </td>
                    <td>
                      {user.is_paused   ? <span className="badge badge-amber">Paused</span>
                        : user.is_active ? <span className="badge badge-green">Active</span>
                        : <span className="badge badge-red">Inactive</span>}
                    </td>
                    <td className="mono-sm col-hide-mobile">
                      {user.last_login ? formatDistanceToNow(user.last_login) : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-dim)', fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
            Click any row to open that user's organization
          </div>
        </div>
      ) : (
        <div className="empty" style={{ paddingTop: 60 }}>
          <Search size={32} style={{ opacity: 0.15 }} />
          <h3>Search for any user</h3>
          <p>Find users across all organizations by name or email.</p>
        </div>
      )}
    </Layout>
  );
}
